import { useRef } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import Selector from "components/select/selector";
import { useTranslation } from "utils/locales/utilityFunctions";

interface MeasurementSectionFieldsProps {
  disableInputs: boolean;
  sectionCount: number;
  backSectionCount?: number;
  frontTitle: string;
  frontHeights: string[];
  setFrontHeights: (heights: string[]) => void;
  measureBothSides?: boolean;
  backTitle?: string;
  backHeights?: string[];
  setBackHeights?: (heights: string[]) => void;
}

const HEIGHT_OPTIONS = Array.from({ length: 111 }, (_, i) => {
  const val = i * 5;
  return {
    id: val.toString(),
    label: `${val} cm`,
    status: "enabled",
  };
});

const MeasurementSectionFields = ({
  disableInputs,
  sectionCount,
  backSectionCount = sectionCount,
  frontTitle,
  frontHeights,
  setFrontHeights,
  measureBothSides = false,
  backTitle,
  backHeights,
  setBackHeights,
}: MeasurementSectionFieldsProps) => {
  const { translate } = useTranslation();
  const lastEnteredHeight = useRef<string | undefined>(undefined);

  const gridDivider = Math.max(3, Math.min(sectionCount, 5));

  return (
    <>
      {/* Front Section */}
      {sectionCount > 0 && <>
      <Grid item xs={12}>
        <Typography variant="h5">{frontTitle}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {Array.from({ length: sectionCount }).map((_, i) => (
            <Grid item xs={6} sm={12 / gridDivider} key={`front-${i}`}>
              <Box
                onMouseDown={() => {
                  if (disableInputs) return;
                  if (!frontHeights[i] && i > 0 && frontHeights[i - 1]) {
                    const updated = [...frontHeights];
                    updated[i] = frontHeights[i - 1];
                    lastEnteredHeight.current = updated[i];
                    setFrontHeights(updated);
                  }
                }}
              >
                <Selector
                  title={`${translate(
                    "input-dialog-raw-wood-tab-measurement-measurement-section-height"
                  )} ${i + 1}`}
                  value={frontHeights[i] || ""}
                  options={HEIGHT_OPTIONS}
                  firstItemCustomValue
                  label
                  fullWidth
                  error={false}
                  disabled={disableInputs}
                  changeHandler={(e) => {
                    const updated = [...frontHeights];
                    updated[i] = e.target.value;
                    if (e.target.value !== "") lastEnteredHeight.current = e.target.value;
                    setFrontHeights(updated);
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>

      </>}

      {/* Optional Back Section */}
      {measureBothSides && backSectionCount > 0 && backHeights && setBackHeights && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5">{backTitle}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {Array.from({ length: backSectionCount }).map((_, i) => (
                <Grid item xs={6} sm={12 / Math.max(3, Math.min(backSectionCount, 5))} key={`back-${i}`}>
                  <Box
                    onMouseDown={() => {
                      if (disableInputs || backHeights[i]) return;
                      const previousHeight = i > 0
                        ? backHeights[i - 1]
                        : lastEnteredHeight.current ?? [...frontHeights.slice(0, sectionCount)].reverse().find(height => height !== "");
                      if (previousHeight !== undefined && previousHeight !== "") {
                        const updated = [...backHeights];
                        updated[i] = previousHeight;
                        lastEnteredHeight.current = previousHeight;
                        setBackHeights(updated);
                      }
                    }}
                  >
                    <Selector
                      title={`${translate(
                        "input-dialog-raw-wood-tab-measurement-measurement-section-height"
                      )} ${i + 1}`}
                      value={backHeights[i] || ""}
                      options={HEIGHT_OPTIONS}
                  firstItemCustomValue
                      label
                      fullWidth
                      error={false}
                      disabled={disableInputs}
                      changeHandler={(e) => {
                        const updated = [...backHeights];
                        updated[i] = e.target.value;
                        if (e.target.value !== "") lastEnteredHeight.current = e.target.value;
                        setBackHeights(updated);
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default MeasurementSectionFields;
